import { useMemo } from 'react';
import { useContent } from 'src/contexts/ContentContext';
import { PosSelect, PosSelectProps } from 'src/core/POS/PosSelect';
import { useConsigneeAccountInfo } from 'src/hooks/api/useConsigneeAccountInfo';
import { EMPTY_GUID } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';

export const ConsigneeSellerAccountSelector = ({
  placeholderText,
  unchangedText,
  excludedSellerAccountIds,
  ...props
}: Omit<PosSelectProps, 'valueOptionsContent'> & {
  allowUnchanged?: boolean;
  unchangedText?: string;
  excludedSellerAccountIds?: string[];
}) => {
  const { data: consigneeAccountInfo } = useConsigneeAccountInfo();
  const unchanged = useContent(ContentId.DontChange);

  const allActiveConsigneeOptions = useMemo(() => {
    if (!consigneeAccountInfo) return {};

    const results = Object.values(consigneeAccountInfo).reduce<
      Record<string, string>
    >((r, cur) => {
      if (!excludedSellerAccountIds?.includes(cur.id)) {
        r[cur.id] = cur.accountName;
      }
      return r;
    }, {});

    if (props.allowUnchanged) {
      results[EMPTY_GUID] = unchangedText ?? unchanged;
    }

    return results;
  }, [
    consigneeAccountInfo,
    excludedSellerAccountIds,
    props.allowUnchanged,
    unchangedText,
    unchanged,
  ]);

  return (
    <PosSelect
      {...props}
      searchable={
        props.searchable != null
          ? props.searchable
          : Object.values(allActiveConsigneeOptions).length > 10
      }
      sortFn={(sellerIdA, sellerIdB) => {
        if (sellerIdA === EMPTY_GUID) {
          return -1;
        }
        if (sellerIdB === EMPTY_GUID) {
          return 1;
        }

        return 0;
      }}
      loading={consigneeAccountInfo == null}
      placeholderText={placeholderText ?? ContentId.Everyone}
      valueOptionsContent={allActiveConsigneeOptions}
      sortBy="key"
    />
  );
};
